import React, { useState } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';

// Pestaña "Credenciales": client_secret de Nawaiam en la API de preproduccion.
// El secreto nuevo se muestra una sola vez; Office guarda solo su hash.
const GET_SECRETOS = gql`
  query GetSecretosIntegrador($entorno: String) {
    getSecretosIntegrador(entorno: $entorno) {
      entorno base clientId vigentes error errorMensaje
      secretos { id etiqueta origen pista creado creadoPor vence revocado revocadoPor ultimoUso estado }
    }
  }
`;
const GENERAR = gql`
  mutation GenerarSecretoIntegrador($etiqueta: String, $venceEnDias: Int!, $anterioresVencenEnDias: Int, $entorno: String) {
    generarSecretoIntegrador(etiqueta: $etiqueta, venceEnDias: $venceEnDias, anterioresVencenEnDias: $anterioresVencenEnDias, entorno: $entorno) {
      secreto anterioresActualizados registro { id etiqueta vence }
    }
  }
`;
const CAMBIAR = gql`
  mutation CambiarVencimientoSecreto($id: ID!, $vence: String, $sinVencimiento: Boolean, $entorno: String) {
    cambiarVencimientoSecreto(id: $id, vence: $vence, sinVencimiento: $sinVencimiento, entorno: $entorno) { id vence estado }
  }
`;
const REVOCAR = gql`
  mutation RevocarSecretoIntegrador($id: ID!, $entorno: String) { revocarSecretoIntegrador(id: $id, entorno: $entorno) { id estado } }
`;

const TZ = 'America/Argentina/Buenos_Aires';
const DIA_MS = 24 * 3600 * 1000;
const VENCE_NUEVO = [[90, '90 días'], [30, '30 días'], [180, '180 días'], [365, '1 año'], [0, 'Sin vencimiento']];
const VENCEN_ACTUALES = [['', 'Siguen como están'], ['30', 'Vencen en 30 días'], ['7', 'Vencen en 7 días'], ['1', 'Vencen en 1 día'], ['0', 'Vencen ahora']];
const ESTADOS = {
  vigente: ['tag--green', 'Vigente'],
  vence_pronto: ['tag--yellow', 'Vence pronto'],
  vencido: ['tag--gray', 'Vencido'],
  revocado: ['tag--red', 'Revocado'],
};

const fecha = (iso) => (iso ? new Date(iso).toLocaleString('es-AR', {
  timeZone: TZ, day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
}) : '');
const hoyBA = () => new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
const diaBA = (iso) => new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(iso));
function faltan(iso) {
  const d = Math.ceil((new Date(iso) - Date.now()) / DIA_MS);
  if (d <= 0) return '';
  return d === 1 ? 'en menos de un día' : `en ${d} días`;
}
const mensajeDe = (e) => (e?.graphQLErrors?.[0]?.message || e?.message || 'Error inesperado.');

export default function CredencialesIntegrador({ entorno = 'pre' }) {
  const { data, loading, error, refetch } = useQuery(GET_SECRETOS, { variables: { entorno }, fetchPolicy: 'network-only' });
  const [generar, { loading: generando }] = useMutation(GENERAR, { fetchPolicy: 'no-cache' });
  const [cambiar, { loading: cambiando }] = useMutation(CAMBIAR, { fetchPolicy: 'no-cache' });
  const [revocar, { loading: revocando }] = useMutation(REVOCAR, { fetchPolicy: 'no-cache' });

  const [form, setForm] = useState(null);           // { etiqueta, venceEnDias, actuales }
  const [nuevo, setNuevo] = useState(null);         // { secreto, vence, anteriores, copiado }
  const [editando, setEditando] = useState(null);   // { id, vence, sinVencimiento }
  const [revocandoId, setRevocandoId] = useState(null);
  const [aviso, setAviso] = useState(null);          // { tipo, texto }

  const r = data?.getSecretosIntegrador;
  const avisar = (tipo, texto) => { setAviso({ tipo, texto }); if (tipo === 'ok') setTimeout(() => setAviso(null), 5000); };

  const onGenerar = async (e) => {
    e.preventDefault();
    try {
      const { data: d } = await generar({ variables: { entorno,
        etiqueta: form.etiqueta.trim() || null,
        venceEnDias: Number(form.venceEnDias),
        anterioresVencenEnDias: form.actuales === '' ? null : Number(form.actuales),
      } });
      const g = d.generarSecretoIntegrador;
      setNuevo({ secreto: g.secreto, vence: g.registro.vence, anteriores: g.anterioresActualizados, actuales: form.actuales, copiado: false });
      setForm(null); setAviso(null);
      refetch();
    } catch (err) { avisar('error', mensajeDe(err)); }
  };
  const copiar = async () => {
    try { await navigator.clipboard.writeText(nuevo.secreto); setNuevo({ ...nuevo, copiado: true }); }
    catch { avisar('error', 'No se pudo copiar: seleccioná el texto y copialo a mano.'); }
  };
  const onCambiar = async (e) => {
    e.preventDefault();
    try {
      await cambiar({ variables: { entorno, id: editando.id, vence: editando.sinVencimiento ? null : editando.vence, sinVencimiento: editando.sinVencimiento } });
      setEditando(null); avisar('ok', 'Vencimiento actualizado. La API lo aplica en menos de un minuto.'); refetch();
    } catch (err) { avisar('error', mensajeDe(err)); }
  };
  const onRevocar = async (id) => {
    try {
      await revocar({ variables: { entorno, id } });
      setRevocandoId(null); avisar('ok', 'Secreto revocado. En menos de un minuto deja de servir para iniciar sesión.'); refetch();
    } catch (err) { setRevocandoId(null); avisar('error', mensajeDe(err)); }
  };

  return (
    <>
      <div className="panel-card cs-intro">
        <div>
          <h2>Credenciales de Nawaiam</h2>
          <p>
            El backend de Nawaiam inicia sesión con el <code>client_id</code> y un <code>client_secret</code> vigente.
            Puede haber más de uno vigente a la vez, para cambiar de secreto sin cortes. Los cambios llegan a la API en menos de un minuto.
          </p>
        </div>
        <div className="cs-intro-datos">
          <span>client_id</span><b className="cs-mono">{r?.clientId || 'nawaiam'}</b>
          <span>Entorno</span><b>{r?.entorno || '—'}</b>
        </div>
      </div>

      {aviso && <div className={`cs-aviso cs-aviso--${aviso.tipo}`} role="status">{aviso.texto}</div>}
      {error && <div className="cs-aviso cs-aviso--error">{mensajeDe(error)}</div>}
      {r?.error && <div className="cs-aviso cs-aviso--error">{r.errorMensaje}</div>}
      {r && !r.error && r.vigentes === 0 && r.secretos.length > 0 && (
        <div className="cs-aviso cs-aviso--error">No hay ningún secreto vigente: Nawaiam no puede iniciar sesión. Generá uno nuevo.</div>
      )}

      {nuevo && (
        <div className="cs-modal-fondo">
          <div className="panel-card cs-modal" role="dialog" aria-modal="true" aria-labelledby="cs-modal-titulo">
            <h2 id="cs-modal-titulo">Secreto generado</h2>
            <p className="cs-modal-alerta">Copialo ahora: no se vuelve a mostrar. Entregalo a Nawaiam solo por un canal cifrado.</p>
            <div className="cs-secreto cs-mono">{nuevo.secreto}</div>
            <div className="cs-modal-datos">
              <span>Vence: <b>{nuevo.vence ? `${fecha(nuevo.vence)} (${faltan(nuevo.vence)})` : 'sin vencimiento'}</b></span>
              {nuevo.actuales !== '' && (
                <span>Secretos actuales con vencimiento nuevo: <b>{nuevo.anteriores}</b></span>
              )}
            </div>
            <div className="cs-acciones">
              <button className="btn-outline" onClick={copiar}>{nuevo.copiado ? 'Copiado ✓' : 'Copiar'}</button>
              <button className="btn-accent" onClick={() => setNuevo(null)}>Ya lo guardé</button>
            </div>
          </div>
        </div>
      )}

      <div className="panel-card cia-seccion">
        <div className="section-head">
          <span className="section-title">Secretos</span>
          {!form && (
            <button className="btn-accent" disabled={!!r?.error}
              onClick={() => setForm({ etiqueta: '', venceEnDias: '90', actuales: '' })}>Generar secreto nuevo</button>
          )}
        </div>

        {form && (
          <form className="cs-form" onSubmit={onGenerar}>
            <label>
              <span>Etiqueta</span>
              <input value={form.etiqueta} maxLength={60} placeholder="Ej.: Entrega a Nawaiam, octubre"
                onChange={(e) => setForm({ ...form, etiqueta: e.target.value })} />
            </label>
            <label>
              <span>El secreto nuevo vence en</span>
              <select value={form.venceEnDias} onChange={(e) => setForm({ ...form, venceEnDias: e.target.value })}>
                {VENCE_NUEVO.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
              </select>
            </label>
            <label>
              <span>Los secretos vigentes</span>
              <select value={form.actuales} onChange={(e) => setForm({ ...form, actuales: e.target.value })}>
                {VENCEN_ACTUALES.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
              </select>
            </label>
            <p className="cs-form-nota">
              {form.actuales === '' && 'Los secretos vigentes no cambian: Nawaiam puede seguir usándolos.'}
              {form.actuales === '0' && 'Los secretos vigentes dejan de servir en menos de un minuto. Nawaiam tiene que pasar al nuevo enseguida.'}
              {form.actuales !== '' && form.actuales !== '0' && `Los secretos vigentes siguen sirviendo ${form.actuales === '1' ? 'un día' : `${form.actuales} días`} más, para que Nawaiam cambie sin cortes. A ninguno se le alarga el vencimiento.`}
            </p>
            <div className="cs-acciones">
              <button type="button" className="btn-outline" onClick={() => setForm(null)}>Cancelar</button>
              <button type="submit" className="btn-accent" disabled={generando}>{generando ? 'Generando…' : 'Generar'}</button>
            </div>
          </form>
        )}

        {loading && !r && <div className="spinner" />}
        {r && !r.error && r.secretos.length === 0 && (
          <div className="empty-state">Todavía no hay secretos en la base. La API usa el del .env hasta que arranque de nuevo.</div>
        )}
        {r && !r.error && r.secretos.length > 0 && (
          <div className="cs-lista">
            {r.secretos.map((s) => {
              const [clase, texto] = ESTADOS[s.estado] || ESTADOS.vigente;
              const activo = s.estado === 'vigente' || s.estado === 'vence_pronto';
              return (
                <div key={s.id} className={`cs-fila ${activo ? '' : 'cs-fila--inactiva'}`}>
                  <div className="cs-quien">
                    <span className="cs-etiqueta">{s.etiqueta || 'Sin etiqueta'}</span>
                    <span className="cs-detalle">
                      {s.origen === 'env' ? 'Cargado desde el .env del servidor' : `Generado por ${s.creadoPor || '?'}`}
                      {s.pista ? <> · termina en <b className="cs-mono">…{s.pista}</b></> : null}
                    </span>
                  </div>
                  <div><span className={`tag ${clase}`}>{texto}</span></div>
                  <div className="cs-fechas">
                    <span>Creado {fecha(s.creado)}</span>
                    <span>
                      {s.revocado ? `Revocado ${fecha(s.revocado)}${s.revocadoPor ? ` por ${s.revocadoPor}` : ''}`
                        : s.vence ? `${s.estado === 'vencido' ? 'Venció' : 'Vence'} ${fecha(s.vence)}${activo ? ` · ${faltan(s.vence)}` : ''}`
                        : 'Sin vencimiento'}
                    </span>
                    <span>{s.ultimoUso ? `Último uso ${fecha(s.ultimoUso)}` : 'Nunca usado'}</span>
                  </div>
                  <div className="cs-botones">
                    {!s.revocado && revocandoId !== s.id && (editando?.id !== s.id) && (
                      <>
                        <button className="btn-outline" onClick={() => {
                          setRevocandoId(null);
                          setEditando({ id: s.id, vence: s.vence && s.estado !== 'vencido' ? diaBA(s.vence) : hoyBA(), sinVencimiento: !s.vence });
                        }}>Vencimiento</button>
                        {activo && <button className="btn-outline cs-btn-peligro" onClick={() => { setEditando(null); setRevocandoId(s.id); }}>Revocar</button>}
                      </>
                    )}
                  </div>

                  {editando?.id === s.id && (
                    <form className="cs-editor" onSubmit={onCambiar}>
                      <label>
                        <span>Vale hasta el final del día</span>
                        <input type="date" value={editando.vence} min={hoyBA()} disabled={editando.sinVencimiento}
                          onChange={(e) => setEditando({ ...editando, vence: e.target.value })} required={!editando.sinVencimiento} />
                      </label>
                      <label className="cs-check">
                        <input type="checkbox" checked={editando.sinVencimiento}
                          onChange={(e) => setEditando({ ...editando, sinVencimiento: e.target.checked })} />
                        <span>Sin vencimiento</span>
                      </label>
                      <div className="cs-acciones">
                        <button type="button" className="btn-outline" onClick={() => setEditando(null)}>Cancelar</button>
                        <button type="submit" className="btn-accent" disabled={cambiando}>{cambiando ? 'Guardando…' : 'Guardar'}</button>
                      </div>
                    </form>
                  )}
                  {revocandoId === s.id && (
                    <div className="cs-editor cs-confirmar">
                      <p>¿Revocar este secreto? En menos de un minuto Nawaiam no podrá usarlo para iniciar sesión. No se puede deshacer.</p>
                      <div className="cs-acciones">
                        <button className="btn-outline" onClick={() => setRevocandoId(null)}>Cancelar</button>
                        <button className="btn-danger" disabled={revocando} onClick={() => onRevocar(s.id)}>{revocando ? 'Revocando…' : 'Revocar'}</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <p className="cia-pie">
        Office guarda solo el hash de cada secreto, en la base {r?.base || 'Nawi3_preprod'}; el secreto en claro se muestra una única vez, al generarlo.
        Un secreto vencido o revocado deja de servir para iniciar sesión y para renovar sesiones; las sesiones ya abiertas duran hasta 15 minutos más.
      </p>
    </>
  );
}
